import { useEffect, useId, useRef, useState, type ClipboardEvent, type ReactNode } from "react";
import { Dialog } from "../../components/ui";
import { requestBlob } from "../../api";

export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGES = 4;

/** The same refusal the server applies: stored text never becomes markup. */
export function plainTextIssue(value: string): string | undefined {
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(value)) return "Use plain text.";
  if (/javascript:/i.test(value) || /data:text\/html/i.test(value)) return "Use plain text.";
  if (/<\s*\/?\s*[a-z!]/i.test(value)) return "Use plain text. Formatting is not supported.";
  return undefined;
}

export function takeImages(current: File[], incoming: File[]): { files: File[]; error?: string } {
  const next = [...current];
  for (const file of incoming) {
    if (!IMAGE_TYPES.includes(file.type)) return { files: current, error: "Only PNG, JPEG, and WebP images are allowed." };
    if (file.size > MAX_IMAGE_BYTES) return { files: current, error: "An image must be 5 MB or smaller." };
    if (file.size === 0) return { files: current, error: "The image is empty." };
    if (next.length >= MAX_IMAGES) return { files: current, error: "A message can include up to 4 images." };
    next.push(file);
  }
  return { files: next };
}

export function pastedImages(event: ClipboardEvent): File[] {
  const out: File[] = [];
  for (const item of event.clipboardData?.items ?? []) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) out.push(file);
    }
  }
  return out;
}

const localUrls = new WeakMap<File, string>();
const localIds = new WeakMap<File, string>();
let nextLocalId = 0;

function localUrl(file: File): string {
  let url = localUrls.get(file);
  if (!url) {
    url = URL.createObjectURL(file);
    localUrls.set(file, url);
  }
  return url;
}

function localId(file: File): string {
  let id = localIds.get(file);
  if (!id) {
    id = `img-${++nextLocalId}`;
    localIds.set(file, id);
  }
  return id;
}

function releaseUrl(file: File) {
  const url = localUrls.get(file);
  if (url) {
    URL.revokeObjectURL(url);
    localUrls.delete(file);
  }
}

/** Chosen images, shown small, removed before they are sent. */
export function ImagePicker({ files, error, onChange, children }: { files: File[]; error?: string; onChange: (files: File[], error?: string) => void; children?: ReactNode }) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [viewing, setViewing] = useState<File | null>(null);
  const shown = useRef<File[]>([]);
  useEffect(() => {
    for (const file of shown.current) if (!files.includes(file)) releaseUrl(file);
    shown.current = files;
  }, [files]);
  useEffect(() => () => shown.current.forEach(releaseUrl), []);

  const remove = (file: File) => onChange(files.filter((f) => f !== file));

  return (
    <div className="help-image-pick">
      {files.length > 0 && (
        <ul className="help-image-row">
          {files.map((file) => (
            <li key={localId(file)} className="help-image-chip">
              <button type="button" className="help-image-open" aria-label={`View ${file.name || "image"}`} onClick={() => setViewing(file)}>
                <img src={localUrl(file)} alt="" />
              </button>
              <button type="button" className="help-image-remove" aria-label={`Remove ${file.name || "image"}`} title="Remove image" onClick={() => remove(file)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="help-image-bar">
        <button type="button" className="btn help-image-add" onClick={() => input.current?.click()}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
            <circle cx="9" cy="10" r="1.6" fill="currentColor" />
            <path d="m4 18 5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          </svg>
          Add images
        </button>
        <input
          ref={input}
          id={inputId}
          tabIndex={-1}
          aria-hidden="true"
          className="help-image-input"
          type="file"
          autoComplete="off"
          accept={IMAGE_TYPES.join(",")}
          multiple
          onChange={(e) => {
            const picked = takeImages(files, [...(e.target.files ?? [])]);
            onChange(picked.files, picked.error);
            e.target.value = "";
          }}
        />
        <span className="muted small help-image-hint">PNG, JPEG, or WebP. Up to 4, 5 MB each.</span>
        {children && <span className="help-image-send">{children}</span>}
      </div>
      <Dialog open={!!viewing} onClose={() => setViewing(null)} title={viewing?.name || "Image"} width={880}>
        {viewing && <img className="help-shot-view" src={localUrl(viewing)} alt={viewing.name || "Chosen image"} />}
      </Dialog>
    </div>
  );
}

/** Images already on a message. A click opens the full image. */
export function MessageImages({ paths }: { paths: string[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (paths.length === 0) return null;
  return (
    <>
      <ul className="help-shots">
        {paths.map((path) => (
          <li key={path}>
            <button type="button" className="help-shot" onClick={() => setOpen(path)} aria-label="View image">
              <RemoteImage path={path} alt="" />
            </button>
          </li>
        ))}
      </ul>
      <Dialog open={!!open} onClose={() => setOpen(null)} title="Image" width={880}>
        {open && <RemoteImage path={open} alt="Attached image" className="help-shot-view" />}
      </Dialog>
    </>
  );
}

function RemoteImage({ path, alt, className }: { path: string; alt: string; className?: string }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let dead = false;
    let objectUrl = "";
    setFailed(false);
    requestBlob(path).then((blob) => {
      if (dead) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(() => { if (!dead) setFailed(true); });
    return () => {
      dead = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);
  if (failed) return <span className="muted small">Image unavailable</span>;
  if (!url) return <span className="skeleton help-shot-waiting" aria-hidden="true" />;
  return <img className={className} src={url} alt={alt} />;
}
